/**
 * SCWT-PM-V2 Phase 3 — PM Agency Profile / Settings tab.
 *
 * Fields: agency_name, contact_email, contact_phone, abn,
 * physical_address {street/suburb/state/postcode}, website, logo.
 * Save posts to PATCH /api/property-manager/agency-profile.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Check, Upload, Trash2 } from "lucide-react";
import api from "@/lib/api";

const AU_STATES = ["NSW", "VIC", "QLD", "SA", "WA", "TAS", "NT", "ACT"];

export default function PmSettingsTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    agency_name: "", contact_email: "", contact_phone: "",
    abn: "", website: "", logo_data_url: "",
    physical_address: { street: "", suburb: "", state: "", postcode: "" },
  });
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/property-manager/agency-profile");
        const ap = data?.agency_profile || {};
        setForm((f) => ({
          ...f,
          agency_name:   ap.agency_name || "",
          contact_email: ap.contact_email || "",
          contact_phone: ap.contact_phone || "",
          abn:           ap.abn || "",
          website:       ap.website || "",
          logo_data_url: ap.logo_data_url || "",
          physical_address: {
            street:   ap.physical_address?.street || "",
            suburb:   ap.physical_address?.suburb || "",
            state:    ap.physical_address?.state || "",
            postcode: ap.physical_address?.postcode || "",
          },
        }));
      } catch (e) {
        setError(e?.response?.data?.detail || "Could not load profile");
      } finally { setLoading(false); }
    })();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAddr = (k, v) => setForm((f) => ({ ...f, physical_address: { ...f.physical_address, [k]: v } }));

  const onLogoPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setError("Logo file must be under 2 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => set("logo_data_url", reader.result);
    reader.onerror = () => setError("Could not read logo file.");
    reader.readAsDataURL(file);
  };

  const clearLogo = () => set("logo_data_url", "");

  const save = async () => {
    setSaving(true); setError(null); setSaved(false);
    try {
      await api.patch("/property-manager/agency-profile", form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save profile");
    } finally { setSaving(false); }
  };

  const canSave = useMemo(() => !saving && !!form.agency_name.trim(), [saving, form.agency_name]);

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-700" /></div>;
  }

  return (
    <div data-testid="pm-settings-tab" className="space-y-6">
      <section className="bg-white rounded-2xl border border-slate-200 p-6">
        <h2 className="text-lg font-bold text-slate-900">Agency profile</h2>
        <p className="text-xs text-slate-500 mt-1">These details appear on landlord invitations and booking pages.</p>

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Agency name" testid="pm-settings-name">
            <input className={inp} value={form.agency_name} onChange={(e) => set("agency_name", e.target.value)}
                    data-testid="pm-settings-agency-name" placeholder="Acme Realty Noosa" />
          </Field>
          <Field label="ABN" testid="pm-settings-abn">
            <input className={inp} value={form.abn} onChange={(e) => set("abn", e.target.value)}
                    data-testid="pm-settings-abn-input" placeholder="11 223 344 556" />
          </Field>
          <Field label="Contact email" testid="pm-settings-email">
            <input className={inp} value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)}
                    data-testid="pm-settings-email-input" type="email" />
          </Field>
          <Field label="Contact phone" testid="pm-settings-phone">
            <input className={inp} value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)}
                    data-testid="pm-settings-phone-input" />
          </Field>
          <Field label="Website" testid="pm-settings-website">
            <input className={inp} value={form.website} onChange={(e) => set("website", e.target.value)}
                    data-testid="pm-settings-website-input" placeholder="https://" />
          </Field>
          <Field label="Logo" testid="pm-settings-logo">
            <div className="flex items-center gap-3">
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml"
                      className="hidden" onChange={onLogoPick} data-testid="pm-settings-logo-file" />
              <button type="button" onClick={() => fileRef.current?.click()}
                      data-testid="pm-settings-logo-btn"
                      className="inline-flex items-center gap-1.5 text-xs border border-slate-300 rounded-full px-3 py-1.5 hover:bg-slate-50">
                <Upload className="w-3.5 h-3.5" /> {form.logo_data_url ? "Replace logo" : "Upload logo"}
              </button>
              {form.logo_data_url && (
                <>
                  <img src={form.logo_data_url} alt="agency logo preview"
                        data-testid="pm-settings-logo-preview"
                        className="h-8 w-8 rounded bg-slate-100 object-contain" />
                  <button type="button" onClick={clearLogo}
                          data-testid="pm-settings-logo-clear"
                          className="inline-flex items-center gap-1 text-xs text-rose-700 hover:text-rose-900">
                    <Trash2 className="w-3.5 h-3.5" /> Remove
                  </button>
                </>
              )}
            </div>
          </Field>
        </div>
      </section>

      <section className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-base font-bold text-slate-900">Physical address</h3>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Street" testid="pm-addr-street">
            <input className={inp} value={form.physical_address.street} onChange={(e) => setAddr("street", e.target.value)}
                    data-testid="pm-addr-street-input" />
          </Field>
          <Field label="Suburb" testid="pm-addr-suburb">
            <input className={inp} value={form.physical_address.suburb} onChange={(e) => setAddr("suburb", e.target.value)}
                    data-testid="pm-addr-suburb-input" />
          </Field>
          <Field label="State" testid="pm-addr-state">
            <select className={inp} value={form.physical_address.state} onChange={(e) => setAddr("state", e.target.value)}
                    data-testid="pm-addr-state-input">
              <option value="">Select…</option>
              {AU_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Postcode" testid="pm-addr-postcode">
            <input className={inp} value={form.physical_address.postcode} onChange={(e) => setAddr("postcode", e.target.value)}
                    data-testid="pm-addr-postcode-input" maxLength={4} />
          </Field>
        </div>
      </section>

      <div className="flex items-center gap-4">
        <button
          data-testid="pm-settings-save-btn"
          disabled={!canSave}
          onClick={save}
          className="inline-flex items-center gap-2 bg-cyan-700 hover:bg-cyan-800 disabled:bg-slate-300 text-white font-semibold rounded-full px-6 py-2.5 text-sm">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
          Save changes
        </button>
        {saved && (
          <span data-testid="pm-settings-saved" className="inline-flex items-center gap-1 text-emerald-700 text-sm">
            <Check className="w-4 h-4" /> Saved
          </span>
        )}
        {error && (
          <span data-testid="pm-settings-error" className="text-sm text-rose-700">{error}</span>
        )}
      </div>
    </div>
  );
}

const inp = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500";

function Field({ label, children, testid }) {
  return (
    <label data-testid={testid} className="block">
      <span className="block text-xs font-semibold text-slate-700 mb-1">{label}</span>
      {children}
    </label>
  );
}
